import Qs from 'qs';
import { CONF } from './environment';
import { deleteSession as deleteGaiaSession } from './gaia-chat';
import { getSearchParamsUrl } from './search_params';

let _cachedIdToken = null;
let _logoutPromise = null;
let _loggingOut = false;

/**
 * Get the current user's username/email from localStorage
 * @returns {string} The current user's username/email
 */
export function getCurrentUserName() {
  return localStorage.getItem('user_name') || '';
}

export function setLoginProvider(newLoginProvider) {
  if (newLoginProvider) {
    localStorage.setItem('loginProvider', newLoginProvider);
  } else {
    localStorage.removeItem('loginProvider');
  }
}

// base64url-encode an ArrayBuffer/TypedArray per RFC 7636 (no padding, URL-safe alphabet)
function base64UrlEncode(buffer) {
  let binary = '';
  new Uint8Array(buffer).forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateCodeVerifier() {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);
  return base64UrlEncode(randomBytes.buffer);
}

async function generateCodeChallenge(codeVerifier) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  return base64UrlEncode(digest);
}

export async function getLoginViaWebEAMUrlAlpha(target, search, path) {
  // Encode the target page after login in state so we will have it available after the redirect
  const encodedTarget = target ? btoa(JSON.stringify({ target })) : undefined;
  const encodedSearch = search ? btoa(JSON.stringify({ search })) : undefined;
  const encodedPath = path ? btoa(JSON.stringify({ path })) : undefined;
  const state = {
    target: encodedTarget,
    search: encodedSearch,
    path: encodedPath,
  };
  const encodedState = btoa(JSON.stringify(state));

  // PKCE (RFC 7636): bind this authorization request to the token exchange so a
  // stolen auth code can't be replayed. Verifier is session-scoped only.
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  sessionStorage.setItem('pkce_code_verifier', codeVerifier);

  const query = {
    ...CONF.webEAMAuthorizeParamsAlpha,
    state: encodedState,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  };
  return `${CONF.authEndpointAlpha}?${Qs.stringify(query)}`;
}

/**
 * Use the Alpha OIDC Auth Code to retrieve JSON Web Tokens (JWT) from the Alpha OIDC Token Endpoint
 * @param {string} code authorization code
 */
export async function initJwt(code) {
  // PKCE: the verifier generated at authorize-time must accompany the token exchange
  const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
  if (!codeVerifier) {
    throw new Error('Missing PKCE code verifier - cannot complete login');
  }

  try {
    const response = await fetch(CONF.tokenEndpointAlpha, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: Qs.stringify({
        grant_type: 'authorization_code',
        client_id: CONF.clientIdAlpha,
        redirect_uri: CONF.redirectUriAlpha,
        code: code,
        code_verifier: codeVerifier,
      }),
    });

    if (!response.ok) {
      throw Error(response.statusText);
    }

    storeJwtData(await response.json());
  } finally {
    sessionStorage.removeItem('pkce_code_verifier');
  }
}

/**
 * In order to keep the table settings and the visibility of the overviews (e.g. dashboard login and active AWS
 * accounts) we store all localStorage items with given prefixes, clear the whole localStorage to get rid of the login
 * information and restore the values that we wanted to keep.
 */
function clearStorage() {
  // Temporary storage of the target localStorage values
  const keeper = {};
  // Prefixes of localStorage values that we want to keep
  const keeperPrefixes = ['DataTables_', 'hidden_', 'customer_portal_aws_', 'quick_links_', 'auth_version'];

  // Create a copy of the localStorage items
  keeperPrefixes.forEach(prefix => {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(prefix)) {
        keeper[key] = localStorage.getItem(key);
      }
    });
  });

  // Clear the whole localStorage
  localStorage.clear();
  sessionStorage.clear();

  // Restore the localStorage items that we wanted to keep
  for (const [key, value] of Object.entries(keeper)) {
    localStorage.setItem(key, value);
  }
}

/**
 * Store the JWT data in the Browser Local Storage
 * @param {object} jwt the JSON web token (JWT)
 * @param {string} jwt.access_token
 * @param {string} jwt.refresh_token
 * @param {string} jwt.id_token
 */
function storeJwtData(jwt) {
  // Store tokens in localStorage for new Alpha OIDC provider
  localStorage.setItem('alpha_access_token', jwt.access_token);
  if (jwt.refresh_token) {
    localStorage.setItem('alpha_refresh_token', jwt.refresh_token);
  }
  if (jwt.id_token) {
    localStorage.setItem('alpha_id_token', jwt.id_token);
  }

  // Store access token in sessionStorage for other micro frontends
  sessionStorage.setItem('user_token', jwt.access_token);
}

/**
 * Check whether the stored access token is expired or will expire within
 * the given buffer window.  Returns true when a proactive refresh is
 * advisable (token missing, malformed, or expiring soon).
 *
 * The check decodes the JWT payload (base64url) to read the `exp` claim
 * — no crypto library needed because we only need the unverified expiry
 * time; actual signature verification is done server-side.
 *
 * @param {number} [bufferSeconds=300] - Seconds before actual expiry to
 *        consider the token as "expiring soon". Defaults to 5 minutes.
 * @param {string} [token] - Token to check. Defaults to the stored access token.
 * @returns {boolean} true if the token should be refreshed
 */
export function isTokenExpiringSoon(bufferSeconds = 300, token = sessionStorage.getItem('user_token')) {
  if (!token) return true;

  try {
    // JWT structure: header.payload.signature — we only need the payload
    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) return true;

    // base64url → base64 → JSON
    const payload = JSON.parse(atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return true;

    const nowSeconds = Math.floor(Date.now() / 1000);
    return nowSeconds >= payload.exp - bufferSeconds;
  } catch {
    // If anything goes wrong decoding, err on the side of refreshing
    return true;
  }
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Calls the Alpha OIDC token endpoint with grant_type=refresh_token.
 *
 * @returns {Promise<boolean>} true if refresh succeeded, false if it failed
 *          (e.g. refresh token expired or missing)
 */
export async function refreshAccessToken() {
  if (_loggingOut) {
    console.debug('[logout] Skipping token refresh: logout in progress.');
    return false;
  }

  const refreshToken = localStorage.getItem('alpha_refresh_token');
  if (!refreshToken) {
    console.debug('No refresh token available, cannot refresh.');
    return false;
  }

  try {
    const response = await fetch(CONF.tokenEndpointAlpha, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: Qs.stringify({
        grant_type: 'refresh_token',
        client_id: CONF.clientIdAlpha,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      console.warn(`Token refresh failed with status ${response.status}`);
      return false;
    }

    const data = await response.json();

    // A logout may have started while this request was in flight — discard
    // the freshly refreshed token instead of writing it back to storage
    if (_loggingOut) {
      console.debug('[logout] Discarding token refresh result: logout started while refresh was in flight.');
      return false;
    }

    storeJwtData(data);
    console.debug('Access token refreshed successfully.');
    return true;
  } catch (err) {
    console.warn('Token refresh request failed:', err);
    return false;
  }
}

/**
 * Store user data from accessible accounts endpoint
 * @param {object} data - Response from getAccessibleAccounts endpoint
 */
export function storeUserData(data) {
  if (data.permissions) {
    localStorage.setItem('permissions', data.permissions);
  }
  if (data.account_areas) {
    localStorage.setItem('account_areas', data.account_areas);
  }
  if (data.user_name) {
    localStorage.setItem('user_name', data.user_name);
    // Update display username in the header navigation bar
    $('.display-username').text(data.user_name);
    $('.display-auth-provider').text('');
  }
}

/**
 * Axios interceptor that adds the authentication token to the request headers.
 * This function is typically used with Axios's interceptors to authenticate API requests.
 *
 * @param {Object} config - The Axios request configuration object
 * @returns {Promise<Object>} A promise that resolves to the modified config object with authorization header
 * @throws {Error} If obtaining the authentication token fails
 */
export function authInterceptor(config) {
  return getAuthToken()
    .then(token => {
      config.headers.Authorization = token;
      return config;
    })
    .catch(error => {
      return Promise.reject(error);
    });
}

/**
 * Retrieves the authentication token.
 *
 * If the token is missing from sessionStorage (e.g. cleared by another tab,
 * or expired and removed), attempts a single refresh before rejecting.
 * This prevents the request interceptor from failing with "No authentication
 * token found" when the response interceptor (which also handles refresh)
 * never gets a chance to run.
 *
 * @async
 * @function getAuthToken
 * @returns {Promise<string>} A promise that resolves to the JWT access token
 * @throws {Error} If the session has expired and refresh failed
 */
export async function getAuthToken() {
  let token = sessionStorage.getItem('user_token');
  if (token) return token;

  // Token missing — attempt a refresh before giving up
  const refreshed = await refreshAccessToken();
  if (refreshed) {
    token = sessionStorage.getItem('user_token');
    if (token) return token;
  }

  throw new Error('No authentication token found');
}

/**
 * Race a promise against a timeout so a hung revocation request can't
 * block logout indefinitely.
 */
function withTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise(resolve => {
    timer = setTimeout(() => {
      console.warn(`[logout] ${label} timed out after ${ms}ms, continuing logout.`);
      resolve();
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Revoke both the refresh token and the access token at the IdP.
 */
async function revokeIdpToken() {
  const refreshToken = localStorage.getItem('alpha_refresh_token');
  const accessToken = localStorage.getItem('alpha_access_token');

  await Promise.allSettled(
    [refreshToken && ['refresh_token', refreshToken], accessToken && ['access_token', accessToken]]
      .filter(Boolean)
      .map(([tokenKind, token]) => revokeSingleIdpToken(tokenKind, token)),
  );

  if (!refreshToken && !accessToken) {
    console.debug('[logout] No Alpha token found, skipping IdP revocation.');
  }
}

async function revokeSingleIdpToken(tokenKind, token) {
  console.debug(`[logout] Revoking ${tokenKind} at IdP...`);

  try {
    const response = await fetch(CONF.tokenRevokeEndpointAlpha, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: Qs.stringify({ client_id: CONF.clientIdAlpha, token, token_type_hint: `${tokenKind}` }),
    });
    console.debug(`[logout] IdP revoke (${tokenKind}) responded with status ${response.status}.`);
  } catch (err) {
    console.warn(`[logout] IdP revoke (${tokenKind}) request failed:`, err);
  }
}

/**
 * Logout from the IdP by revoking tokens, clearing local auth data, and
 * redirecting to the Alpha OIDC end-session endpoint to terminate the SSO
 * session.
 *
 * Safe to call multiple times concurrently (e.g. from multiple 401 interceptors)
 * — subsequent calls reuse the same in-flight logout instead of repeating it.
 * @returns {Promise<void>}
 */
export function logoutFromIdp() {
  if (!_logoutPromise) {
    _loggingOut = true;
    // Reset once settled so a genuinely new logout (not just a concurrent
    // duplicate of an in-flight one) can run again later.
    _logoutPromise = performLogout().finally(() => {
      _logoutPromise = null;
      _loggingOut = false;
    });
  }
  return _logoutPromise;
}

async function performLogout() {
  console.debug('[logout] Starting logout sequence.');

  // Capture the ID token on the very first call so concurrent
  // invocations (e.g. multiple 401 interceptors) can still use it
  // even after clearStorage() wipes localStorage.
  if (!_cachedIdToken) {
    _cachedIdToken = localStorage.getItem('alpha_id_token');
  }

  const idToken = _cachedIdToken;

  // Revoke server-side while the tokens are still in storage, and await it
  // (with a timeout) so the redirect below can't cut the request off mid-flight.
  await withTimeout(revokeIdpToken(), 3000, 'Token revocation');

  // If we have no ID token we can't call end-session – just go
  // straight to the login page.
  if (!idToken) {
    console.debug('[logout] No ID token available, clearing storage and going to login page.');
    clearStorage();
    gotoLoginPage();
    return;
  }

  // Build the end-session URL *before* clearing storage.
  // Include post_logout_redirect_uri so the OIDC provider redirects back
  // to our app after ending the session (without it, the provider returns
  // 204 No Content and the browser stays on the current page).
  const postLogoutUri = `${window.location.protocol}//${window.location.host}/logout`;
  const endSessionUrl = `${CONF.logoutEndpointAlpha}?id_token_hint=${encodeURIComponent(
    idToken,
  )}&post_logout_redirect_uri=${encodeURIComponent(postLogoutUri)}`;

  // Best-effort GAIA session cleanup (fire-and-forget — never blocks logout).
  const gaiaSessionId = sessionStorage.getItem('gaia_session_id');
  if (gaiaSessionId) {
    deleteGaiaSession(gaiaSessionId).catch(() => {});
  }

  // Clear all local auth data.
  clearStorage();

  console.debug('[logout] Redirecting to IdP end-session endpoint.');
  // Redirect to the OIDC provider's end-session endpoint.
  window.location.href = endSessionUrl;
}

/**
 * Forward the user to the login page. In addition, we take the current location of the user
 * from the session storage of the browser and build the custom login link. After a successful
 * login, the user should be forwarded to the same page again.
 */
function gotoLoginPage() {
  if (!getSearchParamsUrl('code')) {
    const targetHash = sessionStorage.getItem('lastHash');
    const targetSearch = sessionStorage.getItem('lastSearch');
    const targetPath = window.location.pathname;
    getLoginViaWebEAMUrlAlpha(targetHash, targetSearch, targetPath).then(loginUrl => {
      window.location.href = loginUrl;
    });
  }
}
